import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SubmitButton } from "@/components/ui/submit-button";
import { Textarea } from "@/components/ui/textarea";
import { AuthContext } from "@/contexts/auth/AuthContext";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { errorHandler } from "@/lib/utils";
import LocationPickerMap from "@/pages/Registrations/ReferencePoints/LocationPickerMap";
import { endCustomerService } from "@/services/api/routeService";
import { zodResolver } from "@hookform/resolvers/zod";
import { MapPin } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";

const applyCNPJMask = (value: string): string => {
  const numbers = value.replace(/\D/g, "");
  const limitedNumbers = numbers.slice(0, 14);
  if (limitedNumbers.length <= 2) {
    return limitedNumbers;
  } else if (limitedNumbers.length <= 5) {
    return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2)}`;
  } else if (limitedNumbers.length <= 8) {
    return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2, 5)}.${limitedNumbers.slice(5)}`;
  } else if (limitedNumbers.length <= 12) {
    return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2, 5)}.${limitedNumbers.slice(5, 8)}/${limitedNumbers.slice(8, 12)}`;
  } else {
    return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2, 5)}.${limitedNumbers.slice(5, 8)}/${limitedNumbers.slice(8, 12)}-${limitedNumbers.slice(12, 14)}`;
  }
};

const removeCNPJMask = (value: string): string => {
  return value.replace(/\D/g, "");
};

const applyCEPMask = (value: string): string => {
  const numbers = value.replace(/\D/g, "");
  const limitedNumbers = numbers.slice(0, 8);

  if (limitedNumbers.length <= 5) {
    return limitedNumbers;
  } else {
    return `${limitedNumbers.slice(0, 5)}-${limitedNumbers.slice(5)}`;
  }
};

const removeCEPMask = (value: string): string => {
  return value.replace(/\D/g, "");
};

const formSchema = z.object({
  name: z.string().min(1, "O nome é obrigatório"),
  document: z.string().optional(),
  code: z.string().optional(),
  email: z.string().email("Email inválido").optional().or(z.literal("")),
  area_code: z.string().optional(),
  phone_number: z.string().optional(),
  address: z.string().min(1, "O endereço é obrigatório"),
  city: z.string().min(1, "A cidade é obrigatória"),
  state: z.string().min(1, "O estado é obrigatório"),
  zip_code: z.string().optional(),
  latitude: z.string().min(1, "A latitude é obrigatória"),
  longitude: z.string().min(1, "A longitude é obrigatória"),
  notes: z.string().optional(),
  cnpj: z.string().optional(),
  client_id: z.number(),
  delivery_time_in_seconds: z.number().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const EndCustomerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const userId = authContext?.userId;
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [isSearchingCEP, setIsSearchingCEP] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      document: "",
      code: "",
      email: "",
      area_code: "",
      phone_number: "",
      address: "",
      city: "",
      state: "",
      zip_code: "",
      latitude: "",
      longitude: "",
      notes: "",
      cnpj: "",
      client_id: userId ?? 0,
      delivery_time_in_seconds: undefined,
    },
  });

  useEffect(() => {
    if (id) {
      const loadEndCustomer = async () => {
        try {
          const response = await endCustomerService.show(Number(id));
          const customer = response.data;
          form.reset({
            name: customer.name,
            document: customer.document || "",
            code: customer.code || "",
            email: customer.email || "",
            area_code: customer.area_code || "",
            phone_number: customer.phone_number || "",
            address: customer.address || "",
            city: customer.city || "",
            state: customer.state || "",
            zip_code: customer.zip_code ? applyCEPMask(customer.zip_code) : "",
            latitude: String(customer.latitude) || "",
            longitude: String(customer.longitude) || "",
            notes: customer.notes || "",
            cnpj: customer.cnpj ? applyCNPJMask(customer.cnpj) : "",
            client_id: customer.client_id,
            delivery_time_in_seconds:
              customer.delivery_time_in_seconds / 60 || undefined,
          });
        } catch (err) {
          errorHandler(err, "Erro ao carregar cliente final");
          navigate("/end-customer");
        }
      };

      loadEndCustomer();
    }
  }, [id, form, navigate]);

  const { handleSubmit, loading } = useFormSubmit({
    id: id ? Number(id) : undefined,
    createService: endCustomerService.store,
    updateService: (id, data) => endCustomerService.update(Number(id), data),
    onSuccess: () => navigate("/end-customer"),
  });

  const onSubmit = async (values: FormValues) => {
    // Check if coordinates are missing but address is filled
    if ((!values.latitude || !values.longitude) && values.address) {
      errorHandler(
        new Error("Coordenadas não geradas"),
        "Você precisa obter as coordenadas clicando no botão 'OBTER COORDENADAS' após preencher o endereço ou selecionar a localização no mapa.",
      );
      return;
    }
    const processedValues = {
      ...values,
      document: values.document || null,
      code: values.code || null,
      email: values.email || null,
      area_code: values.area_code || null,
      phone_number: values.phone_number || null,
      zip_code: values.zip_code ? removeCEPMask(values.zip_code) : null,
      notes: values.notes || null,
      cnpj: values.cnpj ? removeCNPJMask(values.cnpj) : null,
      latitude: Number.parseFloat(values.latitude) || 0,
      longitude: Number.parseFloat(values.longitude) || 0,
      client_id: userId ?? 0,
      delivery_time_in_seconds: values.delivery_time_in_seconds
        ? values.delivery_time_in_seconds * 60
        : 0,
    };

    await handleSubmit(processedValues);
  };

  const searchCEP = async (cep: string, attempt = 1) => {
    if (!cep) return;

    const cleanCEP = removeCEPMask(cep);

    if (cleanCEP.length !== 8) return;

    if (attempt === 1) {
      setIsSearchingCEP(true);
    }

    try {
      const response = await endCustomerService.searchCEP({ cep: cleanCEP });

      if (response.data && response.data.data) {
        const addressData = response.data.data;

        if (addressData.logradouro) {
          form.setValue("address", addressData.logradouro);
        }
        if (addressData.localidade) {
          form.setValue("city", addressData.localidade);
        }
        if (addressData.uf) {
          form.setValue("state", addressData.uf);
        }
        setIsSearchingCEP(false);
      } else {
        throw new Error("Dados não encontrados na resposta");
      }
    } catch (err) {
      if (attempt < 3) {
        const delay = attempt * 500;
        setTimeout(() => {
          searchCEP(cep, attempt + 1);
        }, delay);
      } else {
        setIsSearchingCEP(false);
        errorHandler(
          err,
          `Erro ao buscar CEP após 3 tentativas. Verifique se o CEP está correto.`,
        );
      }
    }
  };

  const geocodeAddress = async () => {
    const address = form.getValues("address");
    const city = form.getValues("city");
    const state = form.getValues("state");

    if (!address || !city || !state) {
      errorHandler(
        new Error("Endereço, cidade e estado são obrigatórios"),
        "Preencha endereço, cidade e estado antes de obter as coordenadas",
      );
      return;
    }

    setIsGeocoding(true);
    try {
      const fullAddress = `${address}, ${city}, ${state}, Brasil`;

      const response = await endCustomerService.searchAddress(fullAddress);
      if (
        !response.errors &&
        response.data &&
        response.data.data.latitude &&
        response.data.data.longitude
      ) {
        form.setValue("latitude", String(response.data.data.latitude));
        form.setValue("longitude", String(response.data.data.longitude));
      } else {
        errorHandler(
          new Error("Endereço não encontrado"),
          "Não foi possível encontrar o endereço. Por favor, verifique se o endereço está correto e tente novamente.",
        );
      }
    } catch (err) {
      errorHandler(err, "Erro ao buscar endereço");
    } finally {
      setIsGeocoding(false);
    }
  };

  const handleMapLocationChange = (lat: number, lng: number) => {
    form.setValue("latitude", String(lat));
    form.setValue("longitude", String(lng));
  };

  const currentLatitude = form.watch("latitude");
  const currentLongitude = form.watch("longitude");

  return (
    <Card className="mx-auto">
      <CardHeader>
        <CardTitle>{id ? "Editar cliente" : "Novo cliente"}</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Nome do cliente" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="cnpj"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CNPJ</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="00.000.000/0001-00"
                        value={field.value || ""}
                        onChange={(e) => {
                          const maskedValue = applyCNPJMask(e.target.value);
                          field.onChange(maskedValue);
                        }}
                        onPaste={(e) => {
                          e.preventDefault();
                          const pastedText = e.clipboardData.getData("text");
                          const maskedValue = applyCNPJMask(pastedText);
                          field.onChange(maskedValue);
                        }}
                        onKeyPress={(e) => {
                          if (!/\d/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Código</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="001" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        placeholder="email@exemplo.com"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-2">
                <FormField
                  control={form.control}
                  name="area_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>DDD</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="55" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefone</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="99999999" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="space-y-4">
              <FormField
                control={form.control}
                name="zip_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CEP</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          {...field}
                          placeholder="01234-567"
                          value={field.value || ""}
                          onChange={(e) => {
                            const maskedValue = applyCEPMask(e.target.value);
                            field.onChange(maskedValue);
                          }}
                          onBlur={(e) => {
                            field.onBlur();
                            const cep = e.target.value;
                            if (cep && removeCEPMask(cep).length === 8) {
                              searchCEP(cep);
                            }
                          }}
                          onPaste={(e) => {
                            e.preventDefault();
                            const pastedText = e.clipboardData.getData("text");
                            const maskedValue = applyCEPMask(pastedText);
                            field.onChange(maskedValue);
                            if (removeCEPMask(maskedValue).length === 8) {
                              searchCEP(maskedValue);
                            }
                          }}
                          onKeyPress={(e) => {
                            if (!/\d/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          disabled={isSearchingCEP}
                        />
                        {isSearchingCEP && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-gray-600 rounded-full"></div>
                          </div>
                        )}
                      </div>
                    </FormControl>
                    <FormMessage />
                    {isSearchingCEP && (
                      <p className="text-sm text-gray-500">Buscando CEP...</p>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Endereço e número *</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Rua, número, bairro..."
                        rows={3}
                        className="dark:border-white"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cidade *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="São Paulo" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Estado *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="SP" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex flex-col gap-2 p-3 border border-amber-300 bg-amber-50 dark:bg-amber-950 dark:border-amber-800 rounded-md">
                <div className="flex items-center gap-2 mb-1">
                  <MapPin className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  <span className="font-medium">
                    Obtenha as coordenadas após preencher o endereço
                  </span>
                </div>
                <p className="text-sm mb-2">
                  1. Preencha os campos <strong>Endereço (com número)</strong>,{" "}
                  <strong>Cidade</strong> e <strong>Estado</strong> acima
                  <br />
                  2. Clique no botão abaixo para gerar automaticamente as
                  coordenadas
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={geocodeAddress}
                  disabled={
                    isGeocoding ||
                    !form.getValues("address") ||
                    !form.getValues("city") ||
                    !form.getValues("state")
                  }
                  className="flex items-center gap-2 bg-amber-200 hover:bg-amber-300 dark:bg-amber-800 dark:hover:bg-amber-700 w-fit"
                >
                  <MapPin className="h-4 w-4" />
                  {isGeocoding ? "Geocodificando..." : "OBTER COORDENADAS"}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <FormLabel>Localização no Mapa</FormLabel>
              <LocationPickerMap
                latitude={currentLatitude}
                longitude={currentLongitude}
                onLocationChange={handleMapLocationChange}
                height="300px"
              />
              <p className="text-sm text-muted-foreground">
                Clique no mapa ou arraste o marcador para definir a localização.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative">
              {(!currentLatitude || !currentLongitude) && (
                <div className="absolute -top-3 right-0 px-2 py-1 bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 text-xs rounded-md flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> Coordenadas pendentes
                </div>
              )}
              <FormField
                control={form.control}
                name="latitude"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Latitude *</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          {...field}
                          type="number"
                          step="any"
                          placeholder="Ex: -23.5505"
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                          className={
                            !field.value
                              ? "border-amber-300 dark:border-amber-700"
                              : ""
                          }
                        />
                        {!field.value && (
                          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                            <MapPin className="h-4 w-4 text-amber-500" />
                          </div>
                        )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="longitude"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Longitude *</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          {...field}
                          type="number"
                          step="any"
                          placeholder="Ex: -46.63333"
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                          className={
                            !field.value
                              ? "border-amber-300 dark:border-amber-700"
                              : ""
                          }
                        />
                        {!field.value && (
                          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                            <MapPin className="h-4 w-4 text-amber-500" />
                          </div>
                        )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observações</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Observações sobre o cliente..."
                      rows={3}
                      className="dark:border-white"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="delivery_time_in_seconds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tempo de entrega (minutos)</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Ex: 60min"
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/end-customer")}
              >
                Cancelar
              </Button>
              <SubmitButton loading={loading} />
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default EndCustomerForm;
